<template>
  <div class="sessions-page">
    <Panel header="Фильтры" toggleable class="filters-panel">
      <div class="filters-grid">
        <div class="filter-field">
          <label>User ID</label>
          <InputText v-model="filters.user_id" class="w-full" />
        </div>
        <div class="filter-field">
          <label>Updated from</label>
          <input v-model="filters.updated_from" type="datetime-local" class="filter-datetime" />
        </div>
        <div class="filter-field">
          <label>Updated to</label>
          <input v-model="filters.updated_to" type="datetime-local" class="filter-datetime" />
        </div>
      </div>
      <div class="filter-actions">
        <ButtonVue label="Применить" icon="pi pi-search" @click="applyFilters" />
        <ButtonVue label="Сбросить" severity="secondary" text @click="resetFilters" />
      </div>
    </Panel>

    <DataTable
      :value="items"
      lazy
      paginator
      :rows="pageSize"
      :total-records="total"
      :loading="loading"
      :rows-per-page-options="[10, 25, 50, 100]"
      data-key="id"
      row-hover
      class="sessions-table"
      @page="onPage"
      @row-click="onRowClick"
    >
      <Column field="id" header="Session ID" style="min-width: 280px">
        <template #body="{ data }">
          <span class="mono">{{ data.id }}</span>
        </template>
      </Column>
      <Column field="user_id" header="User" style="min-width: 120px" />
      <Column field="message_count" header="Messages" style="width: 100px" />
      <Column field="last_message_preview" header="Last message" style="min-width: 200px">
        <template #body="{ data }">
          <span v-if="data.last_message?.role" class="role-tag">{{ data.last_message.role }}:</span>
          {{ data.last_message_preview || '—' }}
        </template>
      </Column>
      <Column field="updated_at" header="Updated" style="min-width: 150px">
        <template #body="{ data }">{{ formatDate(data.updated_at) }}</template>
      </Column>
    </DataTable>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Panel from 'primevue/panel';
import InputText from 'primevue/inputtext';
import { useToast } from 'primevue/usetoast';
import { getSessions } from '@/services/ragMonitorApi';

const router = useRouter();
const toast = useToast();
const loading = ref(false);
const items = ref([]);
const total = ref(0);
const page = ref(1);
const pageSize = ref(25);

const filters = ref({
  user_id: '',
  updated_from: '',
  updated_to: ''
});

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('ru-RU');
}

function buildParams() {
  const f = filters.value;
  const params = { page: page.value, pageSize: pageSize.value };
  if (f.user_id) params.user_id = f.user_id;
  if (f.updated_from) params.updated_from = new Date(f.updated_from).toISOString();
  if (f.updated_to) params.updated_to = new Date(f.updated_to).toISOString();
  return params;
}

async function loadSessions() {
  loading.value = true;
  try {
    const data = await getSessions(buildParams());
    items.value = data.items || [];
    total.value = data.total || 0;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Ошибка', detail: e.message, life: 5000 });
  } finally {
    loading.value = false;
  }
}

function onPage(event) {
  page.value = (event.page ?? 0) + 1;
  pageSize.value = event.rows ?? pageSize.value;
  loadSessions();
}

function applyFilters() {
  page.value = 1;
  loadSessions();
}

function resetFilters() {
  filters.value = { user_id: '', updated_from: '', updated_to: '' };
  page.value = 1;
  loadSessions();
}

function onRowClick(event) {
  router.push(`/rag-monitor/sessions/${event.data.id}`);
}

onMounted(loadSessions);
</script>

<style scoped>
.sessions-page {
  padding-bottom: 1.5rem;
}

.filters-panel {
  margin-bottom: 1rem;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.filter-field label {
  display: block;
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: var(--p-text-muted-color);
}

.filter-datetime {
  width: 100%;
  padding: 0.5rem 0.65rem;
  border: 1px solid var(--p-inputtext-border-color);
  border-radius: var(--p-inputtext-border-radius, 6px);
  background: var(--p-inputtext-background);
  color: var(--p-inputtext-color);
  font: inherit;
  color-scheme: light;
}

.filter-actions {
  display: flex;
  gap: 0.5rem;
}

.sessions-table :deep(.p-datatable-tbody > tr) {
  cursor: pointer;
}

.mono {
  font-family: ui-monospace, monospace;
  font-size: 0.8rem;
  word-break: break-all;
}

.role-tag {
  font-size: 0.75rem;
  font-weight: 600;
  opacity: 0.7;
  margin-right: 0.25rem;
}

.w-full {
  width: 100%;
}
</style>
